import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {PAGE_COMPONENT_EDIT, PAGE_COMPONENT_WRAPPER_EDIT} from "../../../../redux/page/pageTypes";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import TooltipIcon from "../../../application/TooltipIcon";
import LinkIcon from "../../../application/LinkIcon";
import FlipIcon from "../../../application/FlipIcon";
import SettingsIcon from "@mui/icons-material/Settings";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ListenerIcon from "../../../application/ListenerIcon";
import {findXConfigSchema} from "../../components/settings/findXConfigSchema";


import { styled, useTheme, Paper, Fab, IconButton, Tooltip } from '@mui/material';


const PREFIX = 'ComponentWrapper';

const classes = {
    modalButton: `${PREFIX}-modalButton`,
    button: `${PREFIX}-button`,
    buttonPaper: `${PREFIX}-buttonPaper`,
    buttonIcon: `${PREFIX}-buttonIcon`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled(Paper)((
    {
        theme
    }
) => ({
    [`& .${classes.modalButton}`]: {
		position: 'absolute',
		left: "8px",
		bottom: "8px",
		color: theme.palette.grey[500],
		zIndex: 99
	},

    [`& .${classes.button}`]: {
		"&:hover": {
			backgroundColor: "transparent"
		},
		zIndex: 9,
		position: 'absolute',
		margin: 0,
		padding: 0
	},

    [`& .${classes.buttonPaper}`]: {
		// backgroundColor: '#2a2a2b',
		backgroundColor: 'rgba(0,0,0,0)',
		margin: '0',
		padding: '0',
		width: '15px',
		height: '15px'
	},

    [`& .${classes.buttonIcon}`]: {
		color: '#606063',
		position: 'absolute',
		bottom: '0',
		right: '0',
		width: '15px',
		height: '15px'
	}
}));

export function ComponentWrapper({component, xId, dataSourceName, paper, modal, tooltip, link, flip, parseComponent, type, xGridDraggable, xIdHint}) {
	const dispatch = useDispatch();

	const theme = useTheme();

	// App State
	const isEditing = useSelector(state => state.persist.isEditing);

	// Component State
	const [focused, setFocused] = React.useState(false);
	const [modalEnabled, setModalEnabled] = React.useState(false);
	const [tooltipEnabled, setTooltipEnabled] = React.useState(false);
	const [linkEnabled, setLinkEnabled] = React.useState(false);
	const [linkAlwaysVisible, setLinkAlwaysVisible] = React.useState(false);
	const [flipEnabled, setFlipEnabled] = React.useState(false);
	const [flipIconAlwaysVisible, setFlipIconAlwaysVisible] = React.useState(false);
	const [componentFlipped, setComponentFlipped] = useState(false);

	let componentType = type !== undefined ? type.toLowerCase() : '';
	const isDraggable = (xGridDraggable !== undefined ? xGridDraggable : false);

	const componentSchema = findXConfigSchema(null, componentType);
	const isEditable = componentSchema.schema !== undefined ? true : false;

	// Initial Component Settings
	useEffect(() => {
		if (modal && modal.enabled === true) {
			setModalEnabled(true);
		}

		if (tooltip) {
			if (tooltip.configuration.element?.enabled === true) {
				setTooltipEnabled(true);
			} else {
				setTooltipEnabled(false);
			}
		}

		if (link) {
			if (link?.enabled === true) {
				setLinkEnabled(true);
			}
			if (link?.alwaysVisible === true) {
				setLinkAlwaysVisible(true);
			}
		}
	}, [modal, tooltip, link]);

	// Flipped Component Settings
	useEffect(() => {
		if (flip) {
			if (flip?.enabled === true) {
				setFlipEnabled(true);
			}
			if (flip?.alwaysVisible === true) {
				setFlipIconAlwaysVisible(true);
			}
		}
	}, [flip]);

	// This listens for State Changes that will impact the wrapped component
	useEffect(() => {
		// xIdHint only gets set when flipped and is the parent component for xId
		if (isEditing && xId && xIdHint) {
			dispatch({
				type: PAGE_COMPONENT_WRAPPER_EDIT,
				payload: {
					parentId: xIdHint, 	// Parent Component
					childId: xId		// Child Component
				}
			});
		}
	},[componentFlipped])

	function displayModal() {
		if (!modalEnabled)
			return;

		const modelConfig = {
			title: modal.title,
			parent: modal.parent,
			paper: modal.paper,
			button: null,
			content: modal.component ? modal.component : component,
			tooltipContent: tooltip
		}
		dispatch(appSetModalContent(modelConfig));
	}

	function displaySettingsModal() {
		dispatch({
			type: PAGE_COMPONENT_EDIT,
			payload: {
				xId: xId,
				xIdHint: xIdHint
			}
		});
	}

	const handleMouseEnter = (e) => {
		setFocused(true);
	};

	const handleMouseLeave = (e) => {
		setFocused(false);
	};

	const handleClickFlip = e => {
		setComponentFlipped(componentFlipped === true ? false : true);
	};

	const paperStyle = {
		position: 'relative',
		...paper
	}

	// console.log('isDraggable: ', isDraggable);
	// console.log('isEditing: ', isEditing);
	// console.log('xId: ', xId);
	return (
        <React.Fragment>
			{!componentFlipped &&
				<Root style={paperStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>

					{focused && modalEnabled &&
						// <IconButton aria-label="close" style={{...modal.button}} className={classes.modalButton} onClick={displayModal}>
						//     <ZoomOutMapIcon/>
						// </IconButton>
						<Fab size="small" color="primary" aria-label="close" className={classes.modalButton} onClick={displayModal}>
							<ZoomOutMapIcon style={{ color: theme.palette.primary.contrastText }} />
						</Fab>
					}

					{tooltipEnabled &&
						<TooltipIcon tooltip={tooltip} focused={focused} />
					}

					{linkEnabled &&
						<React.Fragment>
							{(focused || linkAlwaysVisible) &&
								<LinkIcon link={link}/>
							}
						</React.Fragment>
					}

					{flipEnabled &&
						<React.Fragment>
							{(focused || flipIconAlwaysVisible) &&
								<FlipIcon flip={flip} handleClick={handleClickFlip}/>
							}
						</React.Fragment>
					}

					{component}

					{isEditing && xId && isEditable &&
						<Tooltip title={type} arrow placement="left">
							<IconButton
                                className={classes.button}
                                style={{bottom: '4px', right: '16px'}}
                                onClick={displaySettingsModal}
                                color="primary"
                                aria-label="open in new window"
                                component="span"
                                size="large">
								<div className={classes.buttonPaper}>
									<SettingsIcon className={classes.buttonIcon}/>
								</div>
							</IconButton>
						</Tooltip>
					}
					{isEditing && xId && isDraggable &&
						<Tooltip title={type} arrow placement="left">
							<IconButton
                                className={classes.button}
                                style={{bottom: '4px', right: '2px'}}
                                color="primary"
                                aria-label="drag"
                                component="span"
                                size="large">
								<div className={["drag-handle", classes.buttonPaper].join(' ')}>
									<DragIndicatorIcon className={classes.buttonIcon}/>
								</div>
							</IconButton>
						</Tooltip>
					}

					{dataSourceName &&
						<ListenerIcon invalid={false} dataSourceNames={[dataSourceName.toLowerCase()]}/>
					}
				</Root>
			}
			{componentFlipped &&
				<Paper style={paperStyle}>

					{flipEnabled &&
						<React.Fragment>
							{(focused || flipIconAlwaysVisible) &&
								<FlipIcon flip={flip} handleClick={handleClickFlip}/>
							}
						</React.Fragment>
					}

					{parseComponent(flip.component, false, null, isDraggable, xId, true, null)}

				</Paper>
			}
		</React.Fragment>
    );
}